import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'wouter';
import Layout from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import { Loader2, Search, Gamepad2, Star, TrendingUp, Zap, Grid3X3, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';


type Tier = 'S' | 'A' | 'B' | 'C';

interface Game {
  id: string;
  gameKey: string;
  gameId: string;
  name: string;
  category: string | null;
  tier: string;
  image?: string | null;
  imageUrl?: string | null;  // Firebase Storage URL from API
  imagePath?: string | null;
  packages?: Array<{ id: string; price: number; amount: string }>;
}

interface TierConfig {
  tier: Tier;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  headerBg: string;
  headerText: string;
  underlineColor: string;
  badgeBg: string;
  badgeText: string;
  gridCols: string; // Tailwind grid class
}

function buildTierConfig(t: (key: string) => string): TierConfig[] {
  return [
    {
      tier: 'S',
      label: t('games.tiers.recommendLabel'),
      sublabel: t('games.tiers.recommendSub'),
      icon: <Star size={20} />,
      headerBg: 'bg-yellow-400',
      headerText: 'text-black',
      underlineColor: 'bg-yellow-400',
      badgeBg: 'bg-yellow-400',
      badgeText: 'text-black',
      gridCols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4', // 4 games per row
    },
    {
      tier: 'A',
      label: t('games.tiers.popularLabel'),
      sublabel: t('games.tiers.popularSub'),
      icon: <TrendingUp size={20} />,
      headerBg: 'bg-black',
      headerText: 'text-white',
      underlineColor: 'bg-black',
      badgeBg: 'bg-black',
      badgeText: 'text-white',
      gridCols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5', // 5 games per row
    },
    {
      tier: 'B',
      label: t('games.tiers.trendingLabel'),
      sublabel: t('games.tiers.trendingSub'),
      icon: <Zap size={20} />,
      headerBg: 'bg-gray-400',
      headerText: 'text-black',
      underlineColor: 'bg-gray-400',
      badgeBg: 'bg-gray-700',
      badgeText: 'text-white',
      gridCols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6', // 6 games per row
    },
    {
      tier: 'C',
      label: t('games.tiers.moreLabel'),
      sublabel: t('games.tiers.moreSub'),
      icon: <Grid3X3 size={20} />,
      headerBg: 'bg-gray-300',
      headerText: 'text-black',
      underlineColor: 'bg-gray-300',
      badgeBg: 'bg-gray-700',
      badgeText: 'text-white',
      gridCols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6', // 6 games per row
    },
  ];
}

interface GameCardProps {
  game: Game;
  index: number;
  onSelect?: (game: Game) => void;
}

function GameCard({ game, index, onSelect }: GameCardProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onSelect) {
      onSelect(game);
    }
  };

  // Priority: Firebase Storage imageUrl from API → Google Play Store image → fallback
  const imageUrl = game.imageUrl || game.image;

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      onClick={handleClick}
      className="cursor-pointer group"
    >
      <div className="relative overflow-hidden bg-gray-100 aspect-square border-2 border-black hover:shadow-lg transition-all">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={game.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <Gamepad2 size={32} className="text-gray-400" />
          </div>
        )}
        
        {/* Category Badge */}
        {game.category && (
          <div className="absolute top-2 right-2 bg-black text-white px-2 py-1 text-xs font-mono font-bold">
            {game.category}
          </div>
        )}
      </div>
      
      {/* Game Info */}
      <div className="mt-2">
        <p className="font-heading font-bold text-sm leading-tight line-clamp-2">
          {game.name}
        </p>
        <p className="text-xs text-gray-500 font-mono mt-1">
          {game.category}
        </p>
      </div>
    </motion.div>
  );
}

interface TierSectionProps {
  config: TierConfig;
  games: Game[];
  onSelectGame: (game: Game) => void;
}

function TierSection({ config, games, onSelectGame }: TierSectionProps) {
  if (!games || games.length === 0) return null;

  return (
    <div className="mb-12">
      {/* Tier Header */}
      <div className={`${config.headerBg} ${config.headerText} px-4 py-3 flex items-center gap-3 mb-4`}>
        {config.icon}
        <span className="font-heading font-bold text-lg tracking-wide">
          {config.label}
        </span>
        <span className="ml-auto font-mono text-sm font-bold">
          {games.length}
        </span>
      </div>

      {/* Colored Underline */}
      <div className={`h-1 ${config.underlineColor} mb-6`}></div>

      {/* Game Grid */}
      <div className={`grid ${config.gridCols} gap-4`}>
        {games.map((game, i) => (
          <GameCard key={game.id} game={game} index={i} onSelect={onSelectGame} />
        ))}
      </div>
    </div>
  );
}

export default function Games() {
  const { t } = useTranslation();
  const TIER_CONFIG = useMemo(() => buildTierConfig(t), [t]);
  const { data: gamesData, isLoading, error: queryError } = trpc.publicGames.listGames.useQuery({
    limit: 100,
    offset: 0,
  });

  const [games, setGames] = useState<Game[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Update games when data arrives
  useEffect(() => {
    if (gamesData?.games && Array.isArray(gamesData.games)) {
      // Convert API response to Game type
      const convertedGames = gamesData.games.map((g: any) => ({
        id: g.gameKey,
        gameKey: g.gameKey,
        gameId: g.gameId,
        name: g.name,
        category: g.category,
        tier: g.tier,
        image: g.image || g.imageUrl,  // Use image from database (Google Play Store or Firebase Storage URL)
        imageUrl: g.image || g.imageUrl,
        imagePath: g.imagePath,
        packages: g.packages,
      }));
      setGames(convertedGames);
      setLoading(false);
    }
  }, [gamesData]);

  // Update loading state
  useEffect(() => {
    if (isLoading) {
      setLoading(true);
    }
  }, [isLoading]);

  // Handle errors
  useEffect(() => {
    if (queryError) {
      setError(queryError.message || t('games.loadFailed'));
      setLoading(false);
    }
  }, [queryError, t]);

  // Filter games by search
  const filteredGames = useMemo(() => {
    if (!searchQuery) return games;
    const query = searchQuery.toLowerCase();
    return games.filter(
      (g) =>
        g.name.toLowerCase().includes(query) ||
        (g.category && g.category.toLowerCase().includes(query))
    );
  }, [games, searchQuery]);

  const isSearching = searchQuery.length > 0;

  // Group games by tier
  const gamesByTier: Record<Tier, Game[]> = useMemo(() => {
    const grouped: Record<Tier, Game[]> = {
      S: [],
      A: [],
      B: [],
      C: [],
    };

    filteredGames.forEach((game) => {
      const tier = (game.tier || 'C') as Tier;
      if (grouped[tier]) {
        // For Tier S (Recommend), only include games with valid icons
        if (tier === 'S') {
          // Skip games without icons
          if (!game.imageUrl && !game.image) {
            return;
          }
          // Skip specific test games
          const gameName = game.name.toLowerCase();
          if (gameName.includes('afk journey') || 
              gameName.includes('arena breakout') ||
              gameName.includes('updated test game')) {
            return;
          }
        }
        grouped[tier].push(game);
      }
    });

    return grouped;
  }, [filteredGames]);

  const [, setLocation] = useLocation();

  const handleSelectGame = (game: Game) => {
    // Navigate to top-up page using gameKey with wouter router
    if (game.gameKey) {
      setLocation(`/topup/${game.gameKey}`);
    } else {
      console.warn('Game key not available for game:', game.name);
    }
  };

  // Debug render
  console.log('[Games] Render state:', { loading, error, gamesCount: games.length, filteredCount: filteredGames.length, isSearching });

  return (
    <Layout>
      {/* Page Header — White Background */}
      <section className="bg-white text-black border-b-4 border-black">
        <div className="container py-12 md:py-16">
          <span className="font-mono text-xs tracking-widest text-gray-500">"ALL GAMES"</span>
          <h1 className="font-heading font-bold text-5xl md:text-6xl tracking-tight mt-2">
            {t('games.title')}
          </h1>
          <p className="text-gray-600 mt-3 max-w-xl">
            {t('games.subtitle', { count: games.length })}
          </p>
        </div>
      </section>

      {/* Search Bar - sticky below navbar (marquee + main nav) */}
      <section className="bg-white text-black border-b-3 border-black sticky z-40 top-[96px] shadow-md">
        <div className="container py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="font-mono text-xs text-gray-500">
              {filteredGames.length} {t('games.countSuffix')}
            </div>
            <div className="relative flex-1 sm:w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder={t('games.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-2 border-black"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Game Content */}
      <section className="bg-white text-black py-12 md:py-16 min-h-[50vh]">
        <div className="container">
          {loading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Loader2 size={32} className="mx-auto mb-4 animate-spin text-black/30" />
              <p className="font-mono text-sm text-black/50">{t('games.loading')}</p>
            </motion.div>
          ) : error ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="text-red-600 font-bold text-lg mb-4">{error}</p>
              <Button onClick={() => window.location.reload()} className="btn-brutal bg-white text-black border-2 border-black">
                {t('games.tryAgain')}
              </Button>
            </motion.div>
          ) : filteredGames.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Gamepad2 size={48} className="mx-auto mb-4 text-black/20" />
              <p className="font-heading font-bold text-2xl mb-2">{t('games.noResults')}</p>
              <p className="text-black/50 font-mono text-sm">{t('games.noResultsHint')}</p>
            </motion.div>
          ) : isSearching ? (
            /* Search Results — Flat Grid */
            <div>
              <div className="mb-6 font-mono text-xs text-gray-500">
                {t('games.searchResults', { query: searchQuery, count: filteredGames.length })}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredGames.map((game, i) => (
                  <GameCard key={game.id} game={game} index={i} onSelect={handleSelectGame} />
                ))}
              </div>
            </div>
          ) : (
            /* Tier-based Layout */
            <>
              {TIER_CONFIG.map((config) => (
                <TierSection key={config.tier} config={config} games={gamesByTier[config.tier]} onSelectGame={handleSelectGame} />
              ))}
            </>
          )}
        </div>
      </section>


    </Layout>
  );
}
